import { useEffect, useRef, useState } from "react";
import { Camera, FileText, Loader2, Paperclip, X } from "lucide-react";
import api, { errorMessage } from "../../lib/api";
import { photoOnly, pickable, uploadTarget } from "../../lib/copilotFiles";
import useIsMobile from "../../hooks/useIsMobile";
import { Button } from "../ui/button";

// « Joindre » on a Copilote card: the person picks files (or takes a photo on a phone); each one goes at once through the SCREEN's
// own upload route (Finance receipts or the module's private files), never to the model. The card keeps their ids and sends them
// with « Accepter »; a file removed here simply stays unattached and is cleaned up like one abandoned on the screen.
export default function ProposalFiles({ spec, files, setFiles, uploading, setUploading, disabled }) {
  const isMobile = useIsMobile();
  const [problems, setProblems] = useState([]);
  const picker = useRef(null);
  const camera = useRef(null);
  const target = uploadTarget(spec);
  const images = spec.accept.some((type) => type.startsWith("image/"));

  // Previews are local object URLs: released when the file leaves the card or the card goes away.
  useEffect(() => () => files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (picked, input) => {
    if (input) input.value = "";
    if (!picked.length || !target) return;
    const { kept, problems: refused } = pickable(spec, files.length, picked);
    setProblems(refused);
    if (!kept.length) return;
    setUploading(true);
    try {
      for (const file of kept) {
        const form = new FormData();
        form.append("file", file);
        try {
          const { data } = await api.post(target.path, form, { params: target.params, headers: { "Idempotency-Key": crypto.randomUUID() } });
          const preview = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
          setFiles((list) => [...list, { id: data.id, name: file.name, preview }]);
        } catch (e) {
          setProblems((list) => [...list, `${file.name} : ${errorMessage(e, "fichier refusé.")}`]);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const remove = (file) => {
    if (file.preview) URL.revokeObjectURL(file.preview);
    setFiles((list) => list.filter((f) => f.id !== file.id));
  };

  const full = files.length >= spec.max_files;
  const accept = spec.accept.join(",");
  return (
    <div className="space-y-2 border-t border-slate-100 px-3 py-2.5" data-testid="copilot-proposal-files">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <div className="text-xs font-medium text-slate-700">
          {spec.label}{" "}
          <span className={spec.required ? "text-amber-700" : "font-normal text-slate-500"}>{spec.required ? "· obligatoire" : "· facultatif"}</span>
        </div>
        <div className="text-2xs text-slate-500">{photoOnly(spec) ? "JPEG, PNG ou WebP" : "PDF ou image"} · {Math.round(spec.max_bytes / (1024 * 1024))} Mo max · {spec.max_files} au plus</div>
      </div>
      {spec.hint && <p className="text-xs text-slate-500">{spec.hint}</p>}
      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Fichiers joints">
          {files.map((f) => (
            <li key={f.id} className="flex max-w-full items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 py-1 pl-1 pr-2 text-xs" data-testid="copilot-proposal-file">
              {f.preview
                ? <img src={f.preview} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
                : <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-white text-slate-400"><FileText size={16} aria-hidden="true" /></span>}
              <span className="min-w-0 truncate">{f.name}</span>
              <button type="button" onClick={() => remove(f)} disabled={disabled} aria-label={`Retirer ${f.name}`}
                className="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-200 hover:text-slate-900 disabled:opacity-50">
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => picker.current?.click()} disabled={disabled || uploading || full}
          data-testid="copilot-proposal-file-add">
          {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Paperclip size={14} className="mr-1.5" />}
          {uploading ? "Envoi…" : files.length ? "Joindre un autre fichier" : "Joindre"}
        </Button>
        {isMobile && images && (
          <Button type="button" variant="outline" size="sm" onClick={() => camera.current?.click()} disabled={disabled || uploading || full}
            data-testid="copilot-proposal-file-camera">
            <Camera size={14} className="mr-1.5" />Prendre une photo
          </Button>
        )}
        <input ref={picker} type="file" className="hidden" multiple={spec.max_files > 1} accept={accept} aria-label={spec.label}
          data-testid="copilot-proposal-file-input" onChange={(e) => send(Array.from(e.target.files || []), e.target)} />
        <input ref={camera} type="file" className="hidden" accept={spec.accept.filter((t) => t.startsWith("image/")).join(",")} capture="environment"
          tabIndex={-1} aria-hidden="true" onChange={(e) => send(Array.from(e.target.files || []), e.target)} />
      </div>
      {problems.length > 0 && (
        <ul role="alert" className="space-y-0.5 text-xs text-red-700" data-testid="copilot-proposal-file-problems">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
    </div>
  );
}
